import { useState } from 'react';
import type { PersonDto } from '@betreuung/shared';
import { ChevronLeft } from 'lucide-react';
import { useDeletePerson, useLogout } from '@/api/mutations';
import { usePersons } from '@/api/queries';
import { ListMessage, SkeletonRows } from '@/components/shared/lists';
import { AlertDialog, AlertDialogBox } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useAppData } from '@/app/appData';
import { useHistoryLayer } from '@/app/useHistoryLayer';
import { useNavigation } from '@/app/navigation';
import { de } from '@/i18n/de';
import { PersonList } from './PersonList';
import { PersonSheet, personErrorMessage } from './PersonSheet';
import { ThemeControl } from './ThemeControl';

/** Which person the sheet is open for; `create` means a new one. */
type SheetTarget = { mode: 'create' } | { mode: 'edit'; person: PersonDto };

/**
 * Full-screen settings layer.
 *
 * Appearance is available to everyone, person management only to administrators
 * — for whom the screen is titled "Verwaltung" instead of "Einstellungen".
 */
export function AdminScreen() {
  const { user } = useAppData();
  const { setAdminOpen } = useNavigation();
  const personsQuery = usePersons();
  const deletePerson = useDeletePerson();
  const logout = useLogout();

  const [sheet, setSheet] = useState<SheetTarget | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PersonDto | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The screen exists only while it is open, so this layer is unconditional.
  useHistoryLayer(true, () => setAdminOpen(false));

  const confirmDelete = async (person: PersonDto) => {
    setPendingDelete(null);
    setDeleteError(null);
    try {
      await deletePerson.mutateAsync(person.id);
    } catch (error) {
      setDeleteError(personErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-surface">
      <div className="nav:mx-auto nav:max-w-[560px]">
        <header className="flex items-center gap-2.5 px-4 pt-4 pb-3">
          <Button
            variant="ghost"
            size="icon"
            className="hit-44 shrink-0"
            aria-label={de.admin.back}
            onClick={() => setAdminOpen(false)}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <h1 className="truncate text-screen font-bold text-fg">
            {user.isAdmin ? de.tabs.admin : de.tabs.settings}
          </h1>
        </header>

        {/* The one screen every account reaches. Saying it once here beats a
            greyed-out control on every tab. */}
        {user.isCaregiver ? null : (
          <p className="px-4 pb-4 text-hint leading-relaxed text-fg-hint">
            {de.admin.viewOnlyNotice}
          </p>
        )}

        <p className="section-label px-4 pb-1.5">{de.admin.appearance}</p>
        <div className="flex px-4 pb-4">
          <ThemeControl />
        </div>

        {user.isAdmin ? (
          <>
            <div className="flex items-center justify-between gap-3 px-4 pb-1.5">
              <p className="section-label">{de.admin.persons}</p>
              <Button size="compact" onClick={() => setSheet({ mode: 'create' })}>
                {de.admin.addPerson}
              </Button>
            </div>

            {personsQuery.isError ? <ListMessage>{de.errors.loadFailed}</ListMessage> : null}
            {personsQuery.isPending ? <SkeletonRows count={4} /> : null}
            {personsQuery.data ? (
              <PersonList
                persons={personsQuery.data}
                onEdit={(person) => setSheet({ mode: 'edit', person })}
                onDelete={(person) => setPendingDelete(person)}
              />
            ) : null}

            {deleteError ? (
              <p role="alert" className="px-4 pt-2.5 text-label text-cal-9">
                {deleteError}
              </p>
            ) : null}

            <p className="px-4 pt-2.5 pb-4 text-hint leading-relaxed text-fg-hint">
              {de.admin.deleteFootnote}
            </p>
          </>
        ) : null}

        <div className="border-t border-divider px-4 pt-4 pb-8">
          <Button
            variant="outline"
            size="dialog"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            {de.admin.signOut}
          </Button>
        </div>
      </div>

      {sheet ? (
        <PersonSheet
          person={sheet.mode === 'edit' ? sheet.person : null}
          persons={personsQuery.data ?? []}
          onClose={() => setSheet(null)}
        />
      ) : null}

      <AlertDialog open={pendingDelete !== null}>
        {pendingDelete ? (
          <AlertDialogBox
            title={de.personSheet.deleteConfirmTitle}
            cancelLabel={de.personSheet.cancel}
            confirmLabel={de.personSheet.deleteConfirm}
            onCancel={() => setPendingDelete(null)}
            onConfirm={() => void confirmDelete(pendingDelete)}
          >
            {de.personSheet.deleteConfirmBody(pendingDelete.name)}
          </AlertDialogBox>
        ) : null}
      </AlertDialog>
    </div>
  );
}
